import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Button } from '@/components/ui/button';
import { Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAppAuth } from '@/contexts/AppAuthContext';
import { formatApiErrorDetail } from '@/lib/appApi';

/**
 * Dashboard admin/user login. Separate from the staff PWA login at
 * `/staff/login`. Lands users on `/` (dashboard) or `?next=...` after
 * a successful sign-in.
 */
export default function AppLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAppAuth();
  const next = params.get('next') || '/';

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const u = await login(email.trim(), password);
      if (!u.app_role) {
        setError('This account does not have dashboard access. Ask an admin to grant it, or use /staff/login.');
        return;
      }
      toast.success(`Welcome, ${u.display_name || u.name || 'there'}!`);
      navigate(next, { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || 'Could not sign in');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-4">
      <Card className="w-full max-w-md shadow-lg" data-testid="app-login-card">
        <CardContent className="pt-8 pb-7 px-7">
          <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <Lock className="h-5 w-5 text-emerald-700" />
          </div>
          <h1 className="text-2xl font-semibold text-center text-gray-900">Flex Living Miami</h1>
          <p className="text-center text-sm text-gray-500 mt-1 mb-6">Sign in to your dashboard</p>

          <form onSubmit={onSubmit} className="space-y-3">
            <Input
              type="email" placeholder="Email" autoComplete="email" required
              value={email} onChange={(e) => setEmail(e.target.value)} data-testid="app-login-email"
            />
            <PasswordInput
              placeholder="Password" autoComplete="current-password" required
              value={password} onChange={(e) => setPassword(e.target.value)} data-testid="app-login-password"
            />
            {error && <p className="text-sm text-destructive text-center" data-testid="app-login-error">{error}</p>}
            <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800" disabled={busy} data-testid="app-login-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
            </Button>
          </form>

          <div className="mt-5 pt-4 border-t border-border/60 text-center space-y-2">
            <Link to="/register" className="block text-xs text-emerald-700 hover:underline" data-testid="app-login-register-link">
              Have an invite code? Set up your account →
            </Link>
            <Link to="/forgot-password" className="block text-xs text-gray-500 hover:underline">
              Forgot password?
            </Link>
            <Link to="/staff/login" className="block text-xs text-gray-400 hover:underline">
              Staff member? Use the staff portal →
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
